import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { sortDirectionOptions } from "../constants";
import {
  type DynamicSort,
  type SortableAttribute,
  SortDirection,
} from "../types";

interface DynamicSortRowProps {
  sort: DynamicSort;
  attribute?: SortableAttribute;
  availableAttributes: SortableAttribute[];
  onUpdate: (updates: Partial<DynamicSort>) => void;
  onRemove: () => void;
  onChangeAttribute: (newAttribute: SortableAttribute) => void;
}

export function DynamicSortRow({
  sort,
  attribute,
  availableAttributes,
  onUpdate,
  onRemove,
  onChangeAttribute,
}: DynamicSortRowProps) {
  const selectableAttributes = attribute
    ? [
        attribute,
        ...availableAttributes.filter((a) => a.field !== attribute.field),
      ]
    : availableAttributes;

  // Get current direction label for display
  const currentDirectionLabel =
    sortDirectionOptions.find((opt) => opt.value === sort.direction)?.label ||
    "Select Direction";

  return (
    <div className="aero:flex aero:items-center aero:gap-2">
      {/* Field select */}
      <Select
        value={sort.field}
        onValueChange={(value) => {
          const newAttr = selectableAttributes.find((a) => a.field === value);
          if (newAttr) {
            onChangeAttribute(newAttr);
          }
        }}
      >
        <SelectTrigger className="aero:h-10 aero:flex-1 aero:min-w-0 aero:rounded aero:border-neutral-300">
          <SelectValue placeholder="Select Field" />
        </SelectTrigger>
        <SelectContent>
          {selectableAttributes.map((attr) => (
            <SelectItem key={attr.id} value={attr.field}>
              {attr.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={
          sort.direction !== undefined ? String(sort.direction) : undefined
        }
        onValueChange={(value) =>
          onUpdate({ direction: Number(value) as SortDirection })
        }
      >
        <SelectTrigger className="aero:h-10 aero:flex-1 aero:min-w-0 aero:rounded aero:border-neutral-300">
          <SelectValue placeholder="Select Direction">
            {sort.direction !== undefined
              ? currentDirectionLabel
              : "Select Direction"}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {sortDirectionOptions.map((opt) => (
            <SelectItem key={opt.value} value={String(opt.value)}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        type="button"
        design="outline"
        size="iconMd"
        className="aero:h-10 aero:w-10 aero:rounded aero:border-[#911300] aero:text-[#911300] aero:hover:bg-red-50 aero:shrink-0 aero:p-2"
        onClick={onRemove}
      >
        <X className="aero:h-5 aero:w-5" />
      </Button>
    </div>
  );
}
